// _commun.js — ce que les six dessins partagent : couleurs de la charte,
// formatage, validation, et la PHRASE D'ABSENCE.
//
// ══ LA RÈGLE QUI GOUVERNE CE DOSSIER ════════════════════════════════════════
//
//   « Une mesure fausse ressemble à une preuve. »
//
// Quand les données sont vides, incomplètes ou malformées, un dessin ne se
// rend PAS : il est remplacé par une phrase qui le dit. Jamais une ligne
// plate, jamais une barre à zéro, jamais un point interpolé — parce qu'un
// lecteur ne distingue pas « pas de données » de « la valeur est zéro ».
//
// ══ AUCUNE COULEUR INVENTÉE ═════════════════════════════════════════════════
//
// Tout vient des variables CSS de src/styles/fbz-admin.css. Les valeurs hex
// ne sont PAS recopiées ici : si la charte bouge, les dessins suivent.

import React from "react";
import { MONTHS } from "../../../lib/format";

/** Les couleurs de la charte admin, par variable CSS (jamais un hex). */
export const COULEUR = Object.freeze({
  navy: "var(--navy)",
  navyD: "var(--navy-d)",
  ciel: "var(--ciel)",
  cielP: "var(--ciel-p)",
  vert: "var(--vert)",
  ambre: "var(--ambre)",
  terra: "var(--terra)",
  gris: "var(--gris)",
  grisD: "var(--gris-d)",
  bord: "var(--bord)",
  fond: "var(--fond)",
});

/** Largeur de référence du viewBox : ~520 px sur ordinateur, ~340 px sur mobile. */
export const LARGEUR = 400;

/** Vrai ssi `v` est un nombre fini — NaN, Infinity, "62" et null sont refusés. */
export function estNombre(v) {
  return typeof v === "number" && Number.isFinite(v);
}

/** Vrai ssi `s` est une chaîne non vide, sans être de la prose (≤ 60 caractères). */
export function estLibelle(s) {
  return typeof s === "string" && s.trim().length > 0 && s.length <= 60;
}

/** Vrai ssi `t` est un moment : chaîne courte ("AAAA-MM", "AAAA-MM-JJ" ou libellé). */
export function estMoment(t) {
  return typeof t === "string" && t.length > 0 && t.length <= 40;
}

/**
 * Formate un nombre à la française : « 3 200 », « 62 », « −1,5 ».
 * Le signe est demandé explicitement (`avecSigne`) pour les deltas.
 * ⚠️ Espace fine insécable entre les milliers (U+202F) — normaliser dans les tests.
 */
export function formatNombre(v, unite, { avecSigne = false } = {}) {
  if (!estNombre(v)) return "—";
  const abs = Math.abs(v);
  const decimales = Number.isInteger(abs) || abs >= 100 ? 0 : 1;
  const [entier, frac] = abs.toFixed(decimales).split(".");
  const milliers = entier.replace(/\B(?=(\d{3})+(?!\d))/g, "\u202f");
  const corps = frac ? `${milliers},${frac}` : milliers;
  const signe = v < 0 ? "−" : avecSigne && v > 0 ? "+" : "";
  const u = unite === "%" ? "\u202f%" : unite ? `\u202f${unite}` : "";
  return `${signe}${corps}${u}`;
}

/**
 * Libellé d'un instant : "2026-08" → « août 2026 », "2026-08-14" → « 14 août 2026 ».
 * Toute autre forme est rendue telle quelle (bornée à 40 caractères, comme les args).
 */
export function libelleMoment(t) {
  const s = String(t ?? "");
  let m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m && MONTHS[Number(m[2]) - 1])
    return `${MONTHS[Number(m[2]) - 1].toLowerCase()} ${m[1]}`;
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m && MONTHS[Number(m[2]) - 1])
    return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1].toLowerCase()} ${m[1]}`;
  return s.slice(0, 40);
}

/** Abréviations françaises usuelles des mois (pas une troncature : « octo » n'est pas un mot). */
const MOIS_COURTS = [
  "janv",
  "févr",
  "mars",
  "avr",
  "mai",
  "juin",
  "juil",
  "août",
  "sept",
  "oct",
  "nov",
  "déc",
];

/** Libellé court d'un instant pour un axe : « août », « 14 août ». */
export function libelleCourt(t) {
  const s = String(t ?? "");
  let m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m && MOIS_COURTS[Number(m[2]) - 1]) return MOIS_COURTS[Number(m[2]) - 1];
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m && MOIS_COURTS[Number(m[2]) - 1])
    return `${Number(m[3])} ${MOIS_COURTS[Number(m[2]) - 1]}`;
  return s.slice(0, 12);
}

/**
 * Coupe un libellé en 2 lignes au plus, sur les espaces, avec « … » si trop long.
 * Sert aux étiquettes sous les barres, où 400 px ne pardonnent rien.
 */
export function couperLibelle(label, maxParLigne) {
  const mots = String(label).trim().split(/\s+/);
  const lignes = [];
  let courante = "";
  let i = 0;
  for (; i < mots.length; i++) {
    const mot = mots[i];
    if (!courante) courante = mot;
    else if ((courante + " " + mot).length <= maxParLigne)
      courante += " " + mot;
    else {
      lignes.push(courante);
      courante = mot;
      if (lignes.length === 2) break;
    }
  }
  if (lignes.length < 2 && courante) lignes.push(courante);
  const resteCoupe =
    lignes.length === 2 && (i < mots.length || courante !== lignes[1]);
  const out = lignes.map((l) =>
    l.length > maxParLigne ? l.slice(0, maxParLigne - 1) + "…" : l,
  );
  if (resteCoupe && !out[1].endsWith("…")) out[1] += "…";
  return out;
}

/**
 * Étiquettes d'axe sans chevauchement : l'indice MARQUÉ (rupture, séparation)
 * est prioritaire ; toute autre étiquette à moins de `minEcart` px de lui
 * s'efface. Vu à 520 px : « août » et « sept » collés en une seule tache.
 */
export function ticksSansCollision(indices, x, marque, minEcart = 34) {
  return [...new Set(indices)]
    .filter(
      (i) =>
        marque == null ||
        i === marque ||
        Math.abs(x(i) - x(marque)) >= minEcart,
    )
    .sort((a, b) => a - b);
}

/** Échelle linéaire [d0, d1] → [r0, r1]. Un domaine plat est élargi de ±1 pour ne jamais diviser par zéro. */
export function echelle(d0, d1, r0, r1) {
  let a = d0;
  let b = d1;
  if (a === b) {
    a -= 1;
    b += 1;
  }
  return (v) => r0 + ((v - a) / (b - a)) * (r1 - r0);
}

/** Bornes [min, max] d'une liste de nombres, avec une marge de 8 % de chaque côté. */
export function bornes(valeurs, { inclureZero = false } = {}) {
  let min = Math.min(...valeurs);
  let max = Math.max(...valeurs);
  if (inclureZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  const marge = (max - min || Math.abs(max) || 1) * 0.08;
  return [min - marge, max + marge];
}

/**
 * La phrase d'absence — ce qui remplace un dessin quand il n'a rien d'honnête à montrer.
 * Elle DIT pourquoi (données vides, incomplètes, incohérentes), elle ne masque pas.
 */
export function PhraseAbsence({ titre, texte }) {
  return (
    <p
      data-testid="dessin-absence"
      role="note"
      style={{
        margin: "6px 0",
        fontSize: 13,
        color: COULEUR.gris,
        fontStyle: "italic",
      }}
    >
      {titre ? `${titre} — ` : ""}
      {texte}
    </p>
  );
}

/**
 * Le cadre commun : une figure, son titre, le SVG accessible, et la conclusion
 * ÉCRITE sous le dessin — la même phrase que l'aria-label, pour que l'œil et
 * le lecteur d'écran lisent la même chose.
 */
export function Figure({ forme, titre, conclusion, hauteur, children }) {
  return (
    <figure
      data-testid={`dessin-${forme}`}
      style={{ margin: "6px 0 10px", padding: 0, maxWidth: 560 }}
    >
      <figcaption
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: COULEUR.navyD,
          margin: "0 0 4px",
        }}
      >
        {titre}
      </figcaption>
      <svg
        role="img"
        aria-label={conclusion}
        viewBox={`0 0 ${LARGEUR} ${hauteur}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "auto", display: "block" }}
        fontFamily="inherit"
      >
        {children}
      </svg>
      <p
        data-testid="dessin-conclusion"
        style={{ margin: "4px 0 0", fontSize: 12.5, color: COULEUR.navyD }}
      >
        {conclusion}
      </p>
    </figure>
  );
}
